import { useCallback, useRef, useState } from 'react';
import { DEFAULT_ENTRY_TYPE, findPayeeByName, isEntryType } from '../utils/entryType';

/**
 * Attribute on a Save control. A blur that lands on it commits nothing: the
 * click is the user's answer, and in the mobile Sheet an opening control would
 * push Save out from under the finger.
 */
export const ENTRY_TYPE_SAVE_ATTR = 'data-entry-type-save';

const sameName = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/**
 * Drives the Entry Type control for one payee field (#1938, epic #1936
 * decision 17, revised 2026-09-29 after the dev test).
 *
 * A payee is committed by Enter (select or create), Tab out, or a click
 * elsewhere; there is no idle timer. Only a brand-new name opens the control
 * by itself, Spending preselected. An existing payee, picked from the list or
 * typed in full, is the chosen payee: it takes its default, or Spending when it
 * has none, and nothing opens (owner, 2026-09-29). The chip (toggle)
 * opens the control for any committed payee; on a payee with a default it is a
 * per-entry override. A pick keeps the value
 * and closes the control; editing the payee text drops the committed payee.
 *
 * A ledger-linked payee (#1944) is locked: the chip shows Transfer, read
 * only, and never opens; the API saves Transfer whatever is sent.
 *
 * The payee picked from the list is remembered until the text is edited
 * again: MUI's blurOnSelect blurs the field in the same click as the pick,
 * before the new name renders, so the blur must not trust the input text.
 *
 * @param {object} args
 * @param {Array} args.payees       current payee list (name, defaultEntryType)
 * @param {boolean} [args.enabled]  false: nothing commits and nothing opens
 */
export const useEntryTypePrompt = ({ payees, enabled = true }) => {
  const [entry, setEntry] = useState(null);
  const payeesRef = useRef(payees);
  payeesRef.current = payees;
  // Payees created this session through Enter-create: they exist, but the
  // caption still says "New payee".
  const createdRef = useRef(new Set());
  const pickedRef = useRef(null);

  const build = useCallback((payee, name, { seedType, autoOpen }) => {
    if (payee?.linkedLedgerId) {
      return {
        payeeName: payee.name,
        mode: 'override',
        defaultType: 'transfer',
        value: 'transfer',
        touched: false,
        open: false,
        locked: true,
      };
    }
    if (payee && isEntryType(payee.defaultEntryType)) {
      return {
        payeeName: payee.name,
        mode: 'override',
        defaultType: payee.defaultEntryType,
        value: isEntryType(seedType) ? seedType : payee.defaultEntryType,
        touched: false,
        open: false,
      };
    }
    const mode = !payee || createdRef.current.has(payee.payeeId) ? 'new' : 'noDefault';
    return {
      payeeName: payee ? payee.name : name,
      mode,
      defaultType: null,
      value: isEntryType(seedType) ? seedType : DEFAULT_ENTRY_TYPE,
      touched: false,
      open: autoOpen && mode === 'new',
    };
  }, []);

  const commit = useCallback((payee, name) => {
    const label = payee ? payee.name : (name || '').trim();
    if (!enabled || !label) {
      setEntry(null);
      return;
    }
    // The same payee committed again (a blur after a pick, a Tab after a
    // choice) keeps what the user already did; only a new payee auto-opens.
    setEntry((prev) => (prev && sameName(prev.payeeName, label) ? prev : build(payee, label, { autoOpen: true })));
  }, [enabled, build]);

  const onPayeePicked = useCallback((payee) => {
    pickedRef.current = payee || null;
    if (!payee) {
      setEntry(null);
      return;
    }
    commit(payee, payee.name);
  }, [commit]);

  const commitText = useCallback((text) => {
    const picked = pickedRef.current;
    if (picked) {
      commit(picked, picked.name);
      return;
    }
    commit(findPayeeByName(payeesRef.current, text), text);
  }, [commit]);

  const onPayeeBlur = useCallback((text, event) => {
    const target = event?.relatedTarget;
    // Focus landing on Save, or inside an Entry Type control (the picker's
    // flyout on the keyboard path, #1943), commits nothing: the control's own
    // pick is the answer, and the text may be a half-typed name.
    if (target && typeof target.closest === 'function'
      && target.closest(`[${ENTRY_TYPE_SAVE_ATTR}], [data-entry-type-control]`)) return;
    commitText(text);
  }, [commitText]);

  // The user typed in the payee field: whatever was committed is gone.
  const onPayeeText = useCallback(() => {
    pickedRef.current = null;
    setEntry(null);
  }, []);

  // Edit mode: the line's payee is committed without opening anything; the
  // chip shows the type already written on the line. Without `payee` the name
  // is looked up here, so a row's edit handler never depends on the payee
  // list (a changing dependency there would re-render every row, #1790).
  const seed = useCallback(({ payee, name, lineType }) => {
    const target = payee || findPayeeByName(payeesRef.current, name);
    pickedRef.current = target || null;
    const label = target ? target.name : (name || '').trim();
    if (!enabled || !label) {
      setEntry(null);
      return;
    }
    setEntry(build(target, label, { seedType: lineType, autoOpen: false }));
  }, [enabled, build]);

  const toggle = useCallback(() => setEntry((p) => (p && !p.locked ? { ...p, open: !p.open } : p)), []);
  const close = useCallback(() => setEntry((p) => (p && p.open ? { ...p, open: false } : p)), []);
  const setValue = useCallback((value) => setEntry((p) => (p ? { ...p, value, touched: true, open: false } : p)), []);
  const reset = useCallback(() => {
    pickedRef.current = null;
    setEntry(null);
  }, []);
  const markCreated = useCallback((payee) => {
    if (payee?.payeeId) createdRef.current.add(payee.payeeId);
  }, []);

  return {
    entry,
    open: Boolean(entry?.open),
    value: entry ? entry.value : null,
    mode: entry ? entry.mode : null,
    payeeName: entry ? entry.payeeName : null,
    chip: entry
      ? { type: entry.value, differs: entry.mode === 'override' && entry.value !== entry.defaultType, locked: Boolean(entry.locked) }
      : null,
    onPayeePicked,
    onPayeeText,
    onPayeeBlur,
    commitText,
    seed,
    toggle,
    close,
    setValue,
    reset,
    markCreated,
  };
};

/**
 * What Save writes for the resolved payee (#1936 decisions 4 and 17):
 * - the committed entry for this payee, no default: its value on the line and
 *   as the payee default (Spending when untouched);
 * - the committed entry for a payee that has a default: its value on the line
 *   only when the user picked one (a per-entry override), else nothing;
 * - nothing committed (a blur that landed on Save): Spending on both for a
 *   payee without a default, nothing for one with a default.
 * Nothing at all when disabled (a Future Expenses budget: the line lands
 * One-Time by precedence and the payee keeps no default) or when `settle` is
 * false (an edit that never touched the payee field or the control).
 *
 * @returns {{ lineType: string|null, payeeDefault: string|null }}
 */
export const entryTypeForSave = ({ enabled = true, entry, payee, payeeName, settle = true }) => {
  const none = { lineType: null, payeeDefault: null };
  if (!enabled || !settle) return none;
  const name = payeeName ?? payee?.name;
  const hasDefault = Boolean(payee && isEntryType(payee.defaultEntryType));
  if (entry && sameName(entry.payeeName, name)) {
    if (entry.mode === 'override' || hasDefault) {
      return entry.touched ? { lineType: entry.value, payeeDefault: null } : none;
    }
    return { lineType: entry.value, payeeDefault: entry.value };
  }
  if (hasDefault) return none;
  return { lineType: DEFAULT_ENTRY_TYPE, payeeDefault: DEFAULT_ENTRY_TYPE };
};
